import {useEffect, useState} from "react";
import * as React from "react";
import {
  Box,
  Container,
  InputLabel,
  Divider,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  IconButton,
  Checkbox,
  Snackbar,
  Chip
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import {PATCH, GET} from "APIclient";
import {useLocation} from "react-router-dom";
import properties from "constants/ApplicationConstants";

const AddTechnology = React.memo(() => {
  const [open, setOpen] = React.useState(false);
  const [snakColor, setSnakColor] = React.useState("");
  const [snakMessage, setSnakMessage] = React.useState("");
  const [primarySkills, setPrimarySkills] = useState([]);
  const [secondarySkills, setSecondarySkills] = useState([]);
  const [newPrimarySkill, setNewPrimarySkill] = useState("");
  const [newSecondarySkill, setNewSecondarySkill] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isEdited, setIsEdited] = useState(false);
  const [isPrimaryEditable, setIsPrimaryEditable] = useState(false);
  const [isSecondaryEditable, setIsSecondaryEditable] = useState(false);
  const [hasAddTechnologyPermission, setHasAddTechnologyPermission] = useState(false);

  const predefinedTechnologies = [
    // --- Programming Languages ---
    "Java",
    "Python",
    "C",
    "C++",
    "C#",
    "JavaScript",
    "TypeScript",
    "Go",
    "PHP",
    "Ruby",
    "Kotlin",
    "Swift",
    "Scala",

    // --- Frontend Development ---
    "React",
    "Angular",
    "Vue.js",
    "Next.js",
    "HTML",
    "CSS",
    "SASS",
    "Tailwind CSS",
    "Bootstrap",

    // --- Backend Development ---
    "Node.js",
    "Express.js",
    ".NET",
    "Spring Boot",
    "Django",
    "Flask",
    "Laravel",
    "NestJS",

    // --- Mobile Development ---
    "Android",
    "iOS",
    "Flutter",
    "React Native",
    "SwiftUI",

    // --- Databases ---
    "MySQL",
    "PostgreSQL",
    "MongoDB",
    "Oracle",
    "SQLite",
    "Firebase",
    "Redis",
    "Cassandra",

    // --- DevOps & Cloud ---
    "AWS",
    "Azure",
    "Google Cloud",
    "Docker",
    "Kubernetes",
    "Jenkins",
    "Terraform",
    "Ansible",
    "Git",
    "GitHub Actions",

    // --- Testing & QA ---
    "Selenium",
    "JUnit",
    "TestNG",
    "Cypress",
    "Postman",
    "JMeter",
    "Playwright",

    // --- Data Science / Analytics ---
    "Machine Learning",
    "Deep Learning",
    "TensorFlow",
    "PyTorch",
    "Pandas",
    "NumPy",
    "Matplotlib",
    "Power BI",
    "Tableau",

    // --- Business & Management ---
    "Business Development",
    "Project Management",
    "Agile",
    "Scrum",
    "JIRA",
    "Confluence",

    // --- Security & Others ---
    "Cybersecurity",
    "Ethical Hacking",
    "Blockchain",
    "Salesforce",
    "SAP",
    "ERP",
    "CRM",
    "UI/UX Design",
    "Figma",
    "Adobe XD",
    // --- HR / Soft Skills ---
    "Human Resource",
    "Communication",
    "Leadership",
    "Teamwork",
    "Problem Solving",
    "Time Management",
    "Negotiation"
  ];

  const location = useLocation();
  const empId = location.state || window.sessionStorage.getItem("empId");

  const url1 = `employee/employee-details/${empId}`;
  const url = "employee/skillset";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await GET(url1);
        const data = response.data ? response.data.data : response;

        if (data && response.data.statusCode === 200) {
          if (!isEdited) {
            const {primarySkillset, secondaryySkillset} = data.skills || {};
            setPrimarySkills(primarySkillset || []);
            setSecondarySkills(secondaryySkillset || []);
          }
          setHasAddTechnologyPermission(data.employee.permissions.includes("add_technology"));
        } else {
          console.error(response.message);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    let intervalId;
    if (!isEdited) {
      intervalId = setInterval(fetchData, 30000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [url1, isEdited]);

  const handleSkillChange = (type, skill) => {
    if (!hasAddTechnologyPermission) return;

    const newSkills = Array.isArray(skill) ? skill : [];

    if (type === "primary") {
      setPrimarySkills(newSkills);
    } else if (type === "secondary") {
      setSecondarySkills(newSkills);
    }
    setIsEdited(true);
  };

  const handleAddSkill = type => {
    if (!hasAddTechnologyPermission) return;

    if (type === "primary" && newPrimarySkill) {
      setPrimarySkills(prevSkills => [...prevSkills, newPrimarySkill].filter(Boolean));
      setNewPrimarySkill("");
      setIsPrimaryEditable(false);
      setIsEdited(true);
    } else if (type === "secondary" && newSecondarySkill) {
      setSecondarySkills(prevSkills => [...prevSkills, newSecondarySkill].filter(Boolean));
      setNewSecondarySkill("");
      setIsSecondaryEditable(false);
      setIsEdited(true);
    }
  };

  const handleDeleteSkill = (type, skill) => {
    if (!hasAddTechnologyPermission) return;
    if (type === "primary") {
      setPrimarySkills(prevSkills => prevSkills.filter(s => s !== skill));
    } else if (type === "secondary") {
      setSecondarySkills(prevSkills => prevSkills.filter(s => s !== skill));
    }
    setIsEdited(true);
  };

  const handlePrimarySkillChange = e => {
    if (!hasAddTechnologyPermission) return;

    const value = e.target.value;
    setNewPrimarySkill(value);
    setIsEdited(!!value.trim());
  };

  const handleSecondarySkillChange = e => {
    if (!hasAddTechnologyPermission) return;

    const value = e.target.value;
    setNewSecondarySkill(value);
    setIsEdited(!!value.trim());
  };

  const handleSubmit = async () => {
    if (!hasAddTechnologyPermission) return;

    const allPrimarySkills = [...primarySkills, newPrimarySkill].filter(Boolean);
    const allSecondarySkills = [...secondarySkills, newSecondarySkill].filter(Boolean);

    const formData = {
      empId: empId,
      primarySkillset: allPrimarySkills,
      secondarySkillset: allSecondarySkills
    };

    try {
      const response = await PATCH(url, formData);
      if (response.data && response.data.statusCode === 200) {
        setSnakColor("success");
        setSnakMessage(response?.data.message);
        setOpen(true);
        setIsEdited(false);
      } else {
        setSnakColor("error");
        setSnakMessage("Error updating skills.");
        setOpen(true);
      }
    } catch (error) {
      setSnakMessage(error.response?.data?.message || "Failed to save skills.");
      setSnakColor("error");
      setOpen(true);
    }
  };

  return (
    <Container>
      {isLoading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <MDBox px={2}>
          <MDBox pt={4} pb={3} px={3}>
            <MDBox component="form" role="form">
              <Box display="flex" justifyContent="space-between" alignItems="flex-end" gap={4}>
                <MDBox flex={1}>
                  <InputLabel
                    sx={{fontWeight: "bold", fontFamily: properties.fontFamily, pb: 2}}
                    id="primary-skill"
                  >
                    Add Primary Skill
                  </InputLabel>
                  <Box display="flex" alignItems="center">
                    {isPrimaryEditable ? (
                      <MDInput
                        value={newPrimarySkill}
                        onChange={handlePrimarySkillChange}
                        placeholder="Add new primary skill"
                        disabled={!hasAddTechnologyPermission}
                        endAdornment={
                          <IconButton
                            onClick={() => handleAddSkill("primary")}
                            disabled={!hasAddTechnologyPermission}
                          >
                            <AddIcon />
                          </IconButton>
                        }
                      />
                    ) : (
                      <Select
                        multiple
                        value={primarySkills}
                        onChange={e => handleSkillChange("primary", e.target.value)}
                        renderValue={() => <em style={{color: "#888"}}>Select Primary Skills</em>}
                        displayEmpty
                        label="Primary Skill"
                        sx={{width: "80%", height: "50px", fontSize: "14px"}}
                        readOnly={!hasAddTechnologyPermission}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              height: "80%"
                            }
                          }
                        }}
                      >
                        {predefinedTechnologies.map(skill => (
                          <MenuItem key={skill} value={skill}>
                            <Checkbox checked={primarySkills.includes(skill)} />
                            <span style={{fontSize: "15px"}}>{skill}</span>
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                    <IconButton
                      onClick={() => setIsPrimaryEditable(true)}
                      disabled={!hasAddTechnologyPermission}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                </MDBox>

                <MDBox flex={1}>
                  <InputLabel
                    sx={{fontWeight: "bold", fontFamily: properties.fontFamily, pb: 2}}
                    id="secondary-skill"
                  >
                    Add Secondary Skill
                  </InputLabel>
                  <Box display="flex" alignItems="center">
                    {isSecondaryEditable ? (
                      <MDInput
                        value={newSecondarySkill}
                        onChange={handleSecondarySkillChange}
                        placeholder="Add new secondary skill"
                        readOnly={!hasAddTechnologyPermission}
                        endAdornment={
                          <IconButton
                            onClick={() => handleAddSkill("secondary")}
                            disabled={!hasAddTechnologyPermission}
                          >
                            <AddIcon />
                          </IconButton>
                        }
                      />
                    ) : (
                      <Select
                        multiple
                        value={secondarySkills}
                        onChange={e => handleSkillChange("secondary", e.target.value)}
                        renderValue={() => <em style={{color: "#888"}}>Select Secondary Skills</em>}
                        displayEmpty
                        label="Secondary Skill"
                        sx={{width: "80%", height: "50px", fontSize: "14px"}}
                        readOnly={!hasAddTechnologyPermission}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              height: "80%"
                            }
                          }
                        }}
                      >
                        {predefinedTechnologies.map(skill => (
                          <MenuItem key={skill} value={skill}>
                            <Checkbox checked={secondarySkills.includes(skill)} />
                            <span style={{fontSize: "15px"}}>{skill}</span>
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                    <IconButton
                      onClick={() => setIsSecondaryEditable(true)}
                      disabled={!hasAddTechnologyPermission}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                </MDBox>
              </Box>

              <Box display="flex" justifyContent="space-between" gap={4} pt={3}>
                <MDBox flex={1}>
                  <InputLabel
                    sx={{fontWeight: "bold", fontFamily: properties.fontFamily, pb: 2}}
                    id="current-primary-skill"
                  >
                    Current Primary Skill
                  </InputLabel>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {primarySkills.map(skill => (
                      <Chip
                        key={skill}
                        label={skill}
                        onDelete={() => handleDeleteSkill("primary", skill)}
                        color="info"
                        variant="outlined"
                        sx={{fontSize: "14px"}}
                        deleteIcon={<CancelIcon />}
                        readOnly={!hasAddTechnologyPermission}
                      />
                    ))}
                  </Box>
                </MDBox>

                <MDBox flex={1}>
                  <InputLabel
                    sx={{fontWeight: "bold", fontFamily: properties.fontFamily, pb: 2}}
                    id="current-secondary-skill"
                  >
                    Current Secondary Skill
                  </InputLabel>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {secondarySkills.map(skill => (
                      <Chip
                        key={skill}
                        label={skill}
                        onDelete={() => handleDeleteSkill("secondary", skill)}
                        color="info"
                        variant="outlined"
                        sx={{fontSize: "14px"}}
                        deleteIcon={<CancelIcon />}
                        readOnly={!hasAddTechnologyPermission}
                      />
                    ))}
                  </Box>
                </MDBox>
              </Box>

              <Divider />

              <MDBox mt={4} mb={1} sx={{display: "grid", gridAutoColumns: "1fr", gap: 1}}>
                <MDBox sx={{gridRow: "1", gridColumn: "4 / 5"}}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    sx={{width: "30ch", background: properties.colors.bgcolorbluethem}}
                    onClick={handleSubmit}
                    disabled={!hasAddTechnologyPermission || !isEdited}
                  >
                    Save
                  </MDButton>
                </MDBox>
              </MDBox>

              <Snackbar
                open={open}
                anchorOrigin={{vertical: "top", horizontal: "right"}}
                autoHideDuration={6000}
                onClose={() => setOpen(false)}
              >
                <Alert
                  onClose={() => setOpen(false)}
                  severity={snakColor === "success" ? "success" : "error"}
                  variant="filled"
                  sx={{width: "100%", color: "#fff"}}
                >
                  {snakMessage}
                </Alert>
              </Snackbar>
            </MDBox>
          </MDBox>
        </MDBox>
      )}
    </Container>
  );
});

export default AddTechnology;
